// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Job polling hook for tracking pipeline execution status.
 * Uses refs for callbacks to avoid interval restarts on callback changes.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { NodeApiClient } from '@/app-shell/services'
import { fetchJobStatus, type JobStatus } from '../services'

/** Use refs for callbacks to avoid restarting polling when references change */
function useStableCallback<T extends (...args: any[]) => any>(
  callback: T | undefined
): React.MutableRefObject<T | undefined> {
  const ref = useRef(callback)
  useEffect(() => {
    ref.current = callback
  }, [callback])
  return ref
}

interface UseJobPollingParams {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  /** Polling interval in ms (default: 2500) */
  pollIntervalMs?: number
  /** Called when any job status changes */
  onStatusChange?: () => Promise<void> | void
  /**
   * Called when all tracked jobs reach a terminal state. The summary lists
   * any jobs that ended in ERROR so callers can surface failures without
   * re-reading job statuses themselves.
   */
  onAllComplete?: (summary: {
    erroredJobIds: string[]
    jobStatuses: Record<string, string>
  }) => void
}

interface UseJobPollingResult {
  /** Add job IDs to track */
  enqueueJobs: (jobIds: string[]) => void
  /** Currently tracked job IDs */
  activeJobIds: string[]
  /** Latest known status for every enqueued job (including terminal statuses) */
  jobStatuses: Record<string, string>
}

/**
 * Poll job statuses and notify on changes.
 *
 * @example
 * ```tsx
 * const { enqueueJobs } = useJobPolling({
 *   apiClient,
 *   orgId: 'umd-org',
 *   workspaceId: 'ora-contracts',
 *   onStatusChange: () => refetchData(),
 *   onAllComplete: () => showSuccess('All jobs completed'),
 * })
 *
 * // After triggering pipeline
 * enqueueJobs(['job-1', 'job-2'])
 * ```
 */
export function useJobPolling({
  apiClient,
  orgId,
  workspaceId,
  pollIntervalMs = 2500,
  onStatusChange,
  onAllComplete,
}: UseJobPollingParams): UseJobPollingResult {
  const [activeJobIds, setActiveJobIds] = useState<string[]>([])
  const [jobStatuses, setJobStatuses] = useState<Record<string, string>>({})
  const lastJobStatusesRef = useRef<Record<string, string>>({})
  const onStatusChangeRef = useStableCallback(onStatusChange)
  const onAllCompleteRef = useStableCallback(onAllComplete)

  const enqueueJobs = useCallback((jobIds: string[]) => {
    if (jobIds.length === 0) return
    setActiveJobIds(prev => Array.from(new Set([...prev, ...jobIds])))
    setJobStatuses(prev => {
      const next = { ...prev }
      jobIds.forEach(jobId => {
        next[jobId] = 'PENDING'
      })
      return next
    })
    jobIds.forEach(jobId => {
      lastJobStatusesRef.current[jobId] = 'PENDING'
    })
  }, [])

  const checkJobStatus = useCallback(async (jobId: string): Promise<JobStatus | null> => {
    try {
      return await fetchJobStatus(apiClient, orgId, workspaceId, jobId)
    } catch (error) {
      console.error(`[useJobPolling] Failed to check status for job ${jobId}`, error)
      return null
    }
  }, [apiClient, orgId, workspaceId])

  useEffect(() => {
    if (activeJobIds.length === 0) return

    let cancelled = false

    const poll = async () => {
      const statuses = await Promise.all(activeJobIds.map(checkJobStatus))
      if (cancelled) return

      let statusChanged = false
      const nextStatuses: Record<string, string> = {}
      const stillActive: string[] = []
      const lastStatuses = lastJobStatusesRef.current

      statuses.forEach((status, index) => {
        const jobId = activeJobIds[index]
        if (!jobId) return

        if (!status) {
          nextStatuses[jobId] = 'ERROR'
          if (lastStatuses[jobId] !== 'ERROR') statusChanged = true
          return
        }

        const normalizedStatus = status.status ?? 'PENDING'
        nextStatuses[jobId] = normalizedStatus
        if (lastStatuses[jobId] !== normalizedStatus) statusChanged = true

        if (normalizedStatus === 'RUNNING' || normalizedStatus === 'PENDING') {
          stillActive.push(jobId)
        }
      })

      const keysChanged = Object.keys(nextStatuses).length !== Object.keys(lastStatuses).length

      if (statusChanged || keysChanged) {
        lastJobStatusesRef.current = nextStatuses
        setJobStatuses(prev => ({ ...prev, ...nextStatuses }))
        await onStatusChangeRef.current?.()
      }

      if (stillActive.length === 0) {
        setActiveJobIds([])
        if (Object.keys(nextStatuses).length > 0) {
          lastJobStatusesRef.current = nextStatuses
        }
        const erroredJobIds = Object.entries(nextStatuses)
          .filter(([, s]) => s === 'ERROR')
          .map(([id]) => id)
        onAllCompleteRef.current?.({ erroredJobIds, jobStatuses: nextStatuses })
      } else if (stillActive.length !== activeJobIds.length) {
        setActiveJobIds(stillActive)
      }
    }

    const interval = setInterval(() => void poll(), pollIntervalMs)
    void poll() // Run immediately

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [activeJobIds, checkJobStatus, pollIntervalMs])

  return { enqueueJobs, activeJobIds, jobStatuses }
}
